import styled from "styled-components"
import { Link } from "react-router-dom"

const HeroWrapper = styled.section`
  position: relative;
  width: 100%;
  min-height: min(820px, 82vh);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 15% 20%, rgba(27, 117, 188, 0.85), rgba(13, 65, 114, 0.6)),
    url("/img/bg.jpg") center/cover;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(140deg, rgba(8, 21, 38, 0.8) 0%, rgba(10, 40, 76, 0.55) 45%, rgba(17, 82, 143, 0.35) 100%);
    z-index: 0;
  }

  @media (max-width: 600px) {
    min-height: auto;
    padding: 4rem 1rem;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 1;

  display: grid;
  gap: clamp(2rem, 4vw, 3rem);
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  align-items: center;

  @media (min-width: 600px) {
    width: min(1120px, 92vw);
  }

  @media (min-width: 1500px) {
    width: min(1420px, 98vw);
  }
`

const Tagline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  padding: 0.5rem 1.1rem;
  border-radius: 2px;
  font-size: 0.85rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`

const Title = styled.h1`
  font-size: clamp(2.4rem, 6vw, 3.3rem);
  margin: 1.15rem 0;
  line-height: 1.08;
  font-weight: 600;
  letter-spacing: -0.04em;
  color: #fff;
`

const Subtitle = styled.p`
  margin: 0;
  margin-bottom: 1.5rem;
  font-size: clamp(1.05rem, 2vw, 1.35rem);
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.88);
  max-width: 620px;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`

const PrimaryAction = styled(Link)`
  padding: 0.95rem 1.9rem;
  border-radius: 2px;
  background: linear-gradient(135deg, #1b75bc 0%, #0d4172 100%);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 18px 40px rgba(15, 63, 115, 0.35);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 24px 50px rgba(15, 63, 115, 0.45);
  }
`

const SecondaryAction = styled(Link)`
  padding: 0.95rem 1.8rem;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(6px);
  transition:
    background-color 0.2s ease,
    transform 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
    transform: translateY(-2px);
  }
`

const Insights = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 1.25rem;
  padding: clamp(1.5rem, 4vw, 2rem);
  border-radius: 2px;
  background: rgba(8, 22, 35, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  box-shadow: 0 18px 48px rgba(8, 21, 38, 0.35);
`

const InsightTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const BenefitList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 1rem;
`

const BenefitItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.9rem;
  align-items: center;
`

const BenefitIcon = styled.span`
  width: 42px;
  height: 42px;
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 195, 255, 0.16);
  border: 1px solid rgba(99, 195, 255, 0.48);
  color: #9addff;
  font-size: 1rem;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(20, 86, 141, 0.35);
`

const BenefitLink = styled.a`
  color: #ffffff;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;

  &:hover,
  &:focus {
    color: #63c3ff;
  }
`

const BenefitDescription = styled.p`
  margin: 0.25rem 0 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.92rem;
  line-height: 1.5;
`

export const Hero = () => {
  return (
    <HeroWrapper>
      <Content>
        <div>
          <Tagline>Portneuf • Couronne ouest de Québec</Tagline>
          <Title>Gestion immobilière locale pour Portneuf et l’ouest de Québec.</Title>
          <Subtitle>
            Nous prenons en charge la location, les demandes des locataires, l’entretien et le suivi administratif de vos immeubles résidentiels.
          </Subtitle>
          <Actions>
            <PrimaryAction to="/faire-une-demande">Parler de votre immeuble</PrimaryAction>
            <SecondaryAction to="/services-gestion-immobiliere">Voir les services</SecondaryAction>
          </Actions>
        </div>
        <Insights>
          <InsightTitle>Ce que vous déléguez</InsightTitle>
          <BenefitList>
            <BenefitItem>
              <BenefitIcon aria-hidden="true">✓</BenefitIcon>
              <div>
                <BenefitLink href="#approche-humaine">Une personne-ressource locale</BenefitLink>
                <BenefitDescription>Un suivi clair de vos priorités, des interventions et des décisions à prendre.</BenefitDescription>
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon aria-hidden="true">✓</BenefitIcon>
              <div>
                <BenefitLink href="#gestion-247">Ligne d’urgence 7 jours sur 7</BenefitLink>
                <BenefitDescription>Un protocole clair pour recevoir, prioriser et coordonner les demandes urgentes.</BenefitDescription>
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon aria-hidden="true">✓</BenefitIcon>
              <div>
                <BenefitLink href="#selection-locataires">Sélection documentée</BenefitLink>
                <BenefitDescription>Des vérifications réalisées selon les critères établis avec chaque propriétaire.</BenefitDescription>
              </div>
            </BenefitItem>
          </BenefitList>
        </Insights>
      </Content>
    </HeroWrapper>
  )
}
